import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export const OptIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consent: true
  });
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // SEO: Prevent indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex';
    document.head.appendChild(metaRobots);
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);
  const validate = () => {
    const newErrors: {
      [key: string]: string;
    } = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.consent) {
      newErrors.consent = 'You must provide consent to continue';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      // Replace this URL with your deployed Google Apps Script Web App URL
      const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwc8u6EUilyu7ibCvp1VW864HrX6RPXv5plF6sH9wPvx7WzcdwuVyvBAfHODglYX9Lm/exec';
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          consent: formData.consent,
          timestamp: new Date().toISOString()
        })
      });
      // no-cors means we can't read the response status,
      // so we optimistically treat any completed fetch as success
      navigate('/success');
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-[#f8faff] flex flex-col items-center justify-center p-4 font-[Inter,sans-serif]" data-id="element-809">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-810">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] p-8 text-center" data-id="element-811">
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-812">Pigeon</h1>
          <p className="text-blue-50 font-medium" data-id="element-813">
            Get updates about your document upload requests
          </p>
        </div>

        {/* Form Section */}
        <div className="p-8" data-id="element-814">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-815">
            {/* Name Field */}
            <div className="space-y-2" data-id="element-816">
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700" data-id="element-817">
                Name
              </label>
              <input type="text" id="name" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-[#2E6BFF] focus:ring-4 focus:outline-none transition-all duration-200`} placeholder="Your Name" data-id="element-818" />
              {errors.name && <p className="text-red-500 text-xs flex items-center mt-1" data-id="element-819">
                  <AlertCircle className="w-3 h-3 mr-1" data-id="element-820" /> {errors.name}
                </p>}
            </div>

            {/* Email Field */}
            <div className="space-y-2" data-id="element-821">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700" data-id="element-822">
                Email Address <span className="text-red-500" data-id="element-823">*</span>
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-[#2E6BFF] focus:ring-4 focus:outline-none transition-all duration-200`} placeholder="Enter Valid Email ID" data-id="element-824" />
              {errors.email && <p className="text-red-500 text-xs flex items-center mt-1" data-id="element-825">
                  <AlertCircle className="w-3 h-3 mr-1" data-id="element-826" /> {errors.email}
                </p>}
            </div>

            {/* Phone Field */}
            <div className="space-y-2" data-id="element-827">
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700" data-id="element-828">
                Phone Number <span className="text-red-500" data-id="element-829">*</span>
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-[#2E6BFF] focus:ring-4 focus:outline-none transition-all duration-200`} placeholder="Enter Phone Number" data-id="element-830" />
              {errors.phone && <p className="text-red-500 text-xs flex items-center mt-1" data-id="element-831">
                  <AlertCircle className="w-3 h-3 mr-1" data-id="element-832" /> {errors.phone}
                </p>}
            </div>

            {/* Consent Checkbox */}
            <div className="space-y-3 pt-2" data-id="element-833">
              <div className="flex items-start" data-id="element-834">
                <div className="flex items-center h-5" data-id="element-835">
                  <input id="consent" type="checkbox" checked={formData.consent} onChange={e => setFormData({
                  ...formData,
                  consent: e.target.checked
                })} className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300 text-[#2E6BFF]" data-id="element-836" />
                </div>
                <label htmlFor="consent" className="ml-3 text-sm text-gray-600 leading-snug" data-id="element-837">
                  By checking this box, you give consent to Pigeon to send SMS
                  related to your document upload requests.
                </label>
              </div>
              {errors.consent && <p className="text-red-500 text-xs flex items-center ml-7" data-id="element-838">
                  <AlertCircle className="w-3 h-3 mr-1" data-id="element-839" /> {errors.consent}
                </p>}

              {/* SMS Disclaimer */}
              <p className="text-xs text-gray-400 ml-7 leading-relaxed" data-id="element-840">
                Message frequency varies. Standard message and data rates may
                apply. Reply STOP to unsubscribe and HELP for assistance.
              </p>
            </div>

            {/* Submit Error */}
            {submitError && <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm text-center" data-id="element-841">
                {submitError}
              </div>}

            {/* Legal Text */}
            <p className="text-xs text-center text-gray-500 px-4" data-id="element-842">
              By clicking Sign up, you agree to Pigeon's{' '}
              <a href="https://pigeondocuments.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#2E6BFF] hover:underline" data-id="element-843">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a href="https://pigeondocuments.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#2E6BFF] hover:underline" data-id="element-844">
                Privacy Policy
              </a>
            </p>

            {/* Submit Button */}
            <button type="submit" disabled={isSubmitting || !formData.consent} className="w-full flex items-center justify-center px-6 py-3.5 text-base font-bold text-white bg-[#2E6BFF] rounded-full hover:bg-[#2558d4] focus:ring-4 focus:ring-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-blue-500/30" data-id="element-845">
              {isSubmitting ? <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-846" />
                  Signing up...
                </> : 'Sign up'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>;
};